import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { MODULES, CONTAINER_SUBS, CONTAINER_SUB_KEYS } from '../../modules';
import { isSharedWorld } from '../../services/activeCompany';
import { menuPill, tabColor } from '../../tabPalette';
import { pageHref, openInNewTab } from '../../services/newTab';   // right-click = a tab of its own (report ab347131)
import { useDashPage, useChatBack } from '../../services/popupHome';   // which page this tab shows, for the pop-ups (report c2846304); and a reduced chat window coming back here (report 14dc81ac)
import { ACCOUNT_COLORS } from '../../utils/accountColors';
import type { FocusDash, FocusLayout, Me } from '../../types';
import { FabHost, FabLift } from '../Fab';  // every page keeps its own bottom-right create + (no host), clear of the dock (reports 18577795, 67f70fbd)
import { QuickLaunchMenu } from '../Portal';
import { MailAppView } from '../MailApp';
import Contact from '../Contact';
import CalendarView from '../Calendar';
import Peak, { InvoiceList, QuotationList, ReceiptList } from '../Peak';
import OutboxView from '../Outbox';
import { PettyCashView } from '../PettyCash';
import { BankStatementView } from '../BankStatement';
import { TakenReturnView } from '../TakenReturn';
import { DocStorageView } from '../DocStorage';
import { ActiveJobsList, ClosedJobsList } from '../Jobs';
import SlfProperty from '../SlfProperty';
import ServiceDesk from '../ServiceDesk';
import Tax from '../Tax';
import AiCounsel from '../AiCounsel';
import PandaMarketing from '../PandaMarketing';
import Bookkeeping from '../Bookkeeping';
import AccountingBooks from '../AccountingBooks';
import FeeBoardView from '../FeeBoard';
import Workflows from '../Workflows';
import Services from '../Services';
import StaffDashboard from '../StaffDashboard';
import FinanceReport from '../FinanceReport';
import PortalAccounts from '../PortalAccounts';
import Bugify from '../Bugify';
import { UserPageView } from '../UserPage';
import { brand, brandOr } from '../../utils/brand';
import { setTabDot } from '../../utils/tabIcon';
import ChatPanel, { useChatUnread } from '../Chat/ChatPanel';
import ChatToast from '../Chat/ChatToast';

// ── Quick Launch: the focus dashboard (/focus) ───────────────────────────────────────────────────────────────────
// Calem, report 0b314fb0: "Allow users to create a focused dashboard in a new browser tab, whereby they can select from
// the top menu 3-6 menu modules … allow them to select from the three-bar menu as well … also allow them to select one
// only (or more if they choose) email client", and to the questions that raised: "One page showing one page at a time,
// with their shortlisted menu clickable, changeable and stacked in a left-hand column … Ask them upon exit whether they
// want to save this dashboard for later / next time." Up to 7 pages since the report's 3rd opening ("Change it to
// allow users up to 7 tabs"), up to 8 since its 5th: "Allow users up to 8 pages rather than 7", up to 10 since report
// ad70923c, up to 12 since report 20e8bdbe and up to 15 since report b878445e (all below). The Portal's rocket opens
// this page in a new tab.
// Down the left: the user's 3–15 pages, then Change and Exit. Beside them: the page clicked, drawn by the very view the
// Portal's content area (a top-menu tab) or its /m/ route (a ≡-menu module) draws, so it works as it does there.
// Change puts the picker in the page's place; Email, once ticked, asks which mailboxes this tab's Email shows — at least
// one — and the store's mailboxFilter holds them while this page is up. The shortlist is kept on the server
// (routes/focusDash.ts) only when the user answers Save on Exit; closing the tab outright with an unsaved one gets the
// browser's own "Leave site?" warning.
// A user keeps several, each under its own name, since Calem's report a41c626a ("Allow users to name their quick launch
// menus when saving them. When you click on the rocket icon (or the bubble) it should bring up a dropdown allowing you
// to choose from pre-saved menus or create new"). The rocket's list opens one as /focus?d=<id>, and + Create new as
// /focus?new=1. ?d= stays in the address beside ?item=, so a reload opens the same dashboard on the same page. A new
// one, or an id the user has no dashboard under, starts in the picker. An opened one's name sits at the top of the
// left column. Exit's question asks for the name: blank for a new dashboard and required, or the saved one to keep or
// change. Save updates the dashboard that was opened, and a name the user already has elsewhere is refused there.
// Report ad70923c: "Allow up to 10 tabs to be selected and ask the user whether they want a side menu or a similar top
// menu to the main system." The picker asks Menu style — Side menu or Top menu — of a new dashboard, whose Done waits
// for the answer, and again under Change. Side menu is the column above, and every dashboard saved before the question
// stays one. Top menu lays the same pages along one bar across the top, each in its top-menu button's colours as on
// the main system's top menu, the open one filled, with the rocket's list of dashboards, Change and Exit at its
// right-hand end; the open page fills the space below. While the picker is open the menu takes its answer at once.
// Its 2nd opening: "When the user selects the modules make sure that all modules that are colorless are grouped
// together in the order consistent with the main top menu". Since then the pages keep the picker's own order rather
// than the order they were ticked in (ordered, below), so the menu reads as the main system's does.
// Report b1e82c89: "Add a bottom menu as well where you have separate tiles and you hover over them from the center
// (and click) like an Apple Mac." Menu style's third answer, Bottom menu (dock): the same pages as tiles on a bar along
// the foot of the window that grow under the pointer, as the Mac's Dock does (Dock, below), and the open page fills
// the space above it.
// Report 6266a6e1: "Show approvals … as an automatic pop-up exactly like the top menu thus, do not make it part of the
// selectable 10 tabs." Approvals is no longer a page to pick, so it never counts toward the 15, and a dashboard saved
// with it drops it on opening. Instead an approver's Approvals button comes into the menu by itself — down the column,
// along the bar or in the dock alike, in its place on the main system's top menu, after Receipt, with that menu's red
// bubble counting what waits — by the main top menu's own rule (Portal: canSeeTab): only for a user the approver flags
// mark (me.isApprover, me.mayApprovePeakDoc; never a name), and only while something waits for them or while it is the
// open page, so approving the last item doesn't pull the page away; it goes once they open another. It opens the page
// the main top menu's Approvals tab opens (OutboxView).
// Report 020e6911: "Make any SLF property, Bugify, menu bubble etc., a green outline with a white background." A page
// with no top-menu colour — every ≡-menu module — wore the app's green as a solid fill: always as a tile in the dock,
// and as the open page down the column or along the bar, where the others had a grey line on nothing. Now, in all three
// menus, it is a green line on white, its icon (and down the column and along the bar its name) in green; the open one
// is told by the dock's dot, or by a line twice as thick (pagePill). The pages that have a top-menu colour, and Change
// and Exit, look as they did.
// Report 20e8bdbe: "Change the name of Quick Launch to 'My Dashboard(s)' and allow users to select up to 12 tabs rather
// than 10." Every word on screen that read Quick Launch — the heading of the rocket's list and the rocket's own name, the
// caption over a dashboard's name and a new one's title here, the Save question's words — reads My Dashboard(s) (the
// ql_ keys); the code keeps the old name. In Thai it is not the literal one, which is already the ≡-menu module My
// Dashboard's (staff_dashboard) and, with no plural to tell them apart, would be the same words. That took a dashboard
// to 12 pages, here (MAX_PAGES) and on the server. With 12 and an approver's Approvals the side column needs about 705px
// of window height before its list scrolls (Change and Exit stay put); the top bar takes a second row in a window under
// about 1,600px wide, as at 10 it did under about 1,400px; the dock at rest is about 775px wide (about 880px since
// report 1d106fd6, below), its height and DOCK_ROOM unchanged.
// Report 32621dcf: "Make a WhatsApp/Telegram-like messaging system for any/all internal communications … Allow the
// users to add this to their dashboard without using one of their 12 alowable tabs", and to the question that raised:
// the "chat button can be in the menu dock (top, side, or bottom) and opens a pop-up over the current page so that it
// doesn't use one of the 12 pages." So Chat is no page either: the picker never offers it, a dashboard never holds it,
// and it counts toward neither the 3 nor the 15. Its button comes into the menu by itself, as an approver's Approvals
// does (report 6266a6e1) but for everybody and always — last down the column, last along the bar, and the last tile
// before the dock's divider — with that menu's red bubble counting everything unread, asked for every 30 seconds
// (components/Chat/ChatPanel: useChatUnread). It opens the chat pop-up over whatever page is showing; the page itself
// does not change. It is one more button in each menu: the side column needs about 40px more window height before its
// list scrolls, the top bar about 90px more width before it takes a second row, and the dock at rest is about 57px
// wider (one tile and its gap); the dock's own height and DOCK_ROOM are untouched.
// Report 1d106fd6: "Make the bottom deck tiles/bubbles 15% larger than they are currently." The dock's tiles are 15%
// larger: 51px a side at rest and 83px right under the pointer, from 44 and 72 (DOCK_TILE, DOCK_PEAK). A tile's icon is
// half its side and its corners are rounded by 24% of it, so both grow with it; so do the bar's height (63 → 70px) and
// the room the page leaves under itself for the dock (DOCK_ROOM, 77 → 84px). How far the magnify reaches, the gaps, the
// open page's dot, the name bubble, the red count bubble and the bar's own look are as they were; the side column and
// the top bar are not touched.
// Report b878445e: "Allow users to have up to 15 pages in their dashboard." A dashboard now holds up to 15, here
// (MAX_PAGES) and on the server; the 3 it takes at least, and Approvals and Chat staying outside the count, are as they
// were. Three more pages want more room: down the column the list needs about 125px more window height before it
// scrolls (a page's button and its gap is 42px), along the bar about 300px more width before it takes a second row, and
// the dock at rest is about 170px wider (three tiles and their gaps, 57px each). Nothing else in any of the three menus
// changes, and a dashboard saved with 12 or fewer opens as it always did.
// Report f4ef1615: "Make the side menu collapsible into icons if the user prefers by moving the left-hand panel to the
// left or to the right, the user can make the menu bubbles larger or smaller, right down to icon size." The side
// column, 210px and fixed until then, has a handle on its right-hand edge that a mouse, a finger or a pen drags from
// 56px to 320px (SIDE_W, below), its buttons narrowing and widening with it. Narrower than 120px every button is its
// icon alone — the pages, Approvals and Chat, with the red count on the button's corner, and Change and Exit — a page's
// name in its tooltip (Chat, Change and Exit keep the tooltips they had), and the logo alone stands at the top, the
// dashboard's name in its tooltip. A double-click on the edge switches between icons only and the full 210px. The
// width is each person's own, kept in this browser under their login, so every dashboard they open here opens at it.
// The top bar and the dock are not touched.
// Report 18577795 (2nd opening): "In dashboard mode only, for each user bring the +circle on each menu page over to the
// menu button, whether it be a side, bottom, or top menu, float the +circle (to add a new quotation as an example)", and
// to the question that raised: "The circle should land right next to that menu option whether the menu is running across
// the top of the tab, left of the tab, or bottom dock." Every list page puts its create button in the one floating
// bottom-right + (components/Fab), which here belonged to the page alone: it never reached this dashboard's menu, and
// with the dock it sat on top of it. So, in dashboard mode only, the open page handed its + over (Fab: FabHost) and
// drew none of its own, and this page floated it against that page's own button in whichever menu was up — off the side
// column's button on its right-hand edge, icons only included; immediately after the button on the top bar; and on the
// dock tile's top-right corner, where it rode the magnify. It did what the page's own + did, carried the page's tooltip
// and wore the page's colour, so there was nothing new to read. A page with no + of its own (the User page, Approvals,
// a ≡-menu module without one) handed nothing over and its button carried none, and nothing in any menu shifted or
// changed size to make room. Outside a dashboard — every /m/ route, the Portal's Dashboard — the + stayed in the
// bottom-right corner exactly as it was. The two openings below took this back, menu by menu.
// Report 18577795 (3rd opening): "Remove this function on everything other than the side menu. Remove it from the top
// menu and the bottom dock." So the + floated on the menu button in a Side menu dashboard only. The take-over hand went
// to the page under a side column alone (FabHost below, null under the other two), so a Top menu or Bottom menu
// dashboard handed none and every page drew its own + in its bottom-right corner again, label bubble included, exactly
// as it does on its own /m/ route; the top bar's buttons and the dock's tiles carried nothing. Under the dock that
// corner is where the bar stands, which is how the + came to be moved in the first place, so the page is told how much
// room the dock takes (FabLift, DOCK_ROOM) and lifts its + that far — clear of the bar, not behind it.
// Report 67f70fbd ("Put the add button back into its original spot in the bottom right corner of the page"):
// the side column was the last menu still carrying it, so it goes from there too and the + is back in the page's own
// bottom-right corner in every dashboard, whichever menu is up — the same corner, the same size and the same label
// bubble as on the page's own /m/ route. No page is handed a take-over hand any more (FabHost: null in all three
// layouts), and nothing floats against a menu button: the top bar's buttons, the side column's buttons and the dock's
// tiles all carry nothing. The lift stays, so under a dock the restored circle still sits above the bar instead of
// behind it. **The + belongs in that corner and nowhere else — never put it back on the top bar or the dock.**

const MIN_PAGES = 3;
const MAX_PAGES = 15;  // the server's limit too (routes/focusDash.ts: MAX_ITEMS)
const MAX_NAME = 40;   // the server's limit on a dashboard's name (routes/focusDash.ts)

type Page = { key: string; icon: string; label: string };
// layout null: not chosen yet — a new dashboard, whose picker asks.
type Dash = { items: string[]; mailboxes: string[]; layout: FocusLayout | null };
type Mailbox = { id: string; name: string; email: string; color: string };

// Menu style's three answers, as the picker offers them.
const LAYOUTS: { key: FocusLayout; icon: string; label: string; hint: string }[] = [
  { key: 'side', icon: 'layout-sidebar', label: 'ql_layout_side', hint: 'ql_layout_side_hint' },
  { key: 'top', icon: 'layout-navbar', label: 'ql_layout_top', hint: 'ql_layout_top_hint' },
  { key: 'bottom', icon: 'layout-bottombar', label: 'ql_layout_bottom', hint: 'ql_layout_bottom_hint' },
];

// The top menu's tabs, in their order on screen (Portal/index.tsx: the Email / Contacts / Calendar box, Quotation …
// Approvals, Petty Cash, Bank, Document, then Jobs at the far right), each under the name its tab goes by — Document's
// two are Tracking and Filing there. A dashboard's pages keep this order too (ordered, below). Approvals is here for its
// place in that order only: the picker leaves it out, and its button comes by itself (report 6266a6e1, above).
const TOP_PAGES: Page[] = [
  { key: 'email', icon: 'mail', label: 'tab_email' },
  { key: 'contact', icon: 'address-book', label: 'tab_contacts' },
  { key: 'calendar', icon: 'calendar-month', label: 'mod_calendar' },
  { key: 'quotation', icon: 'file-dollar', label: 'mod_quotation' },
  { key: 'invoice', icon: 'file-invoice', label: 'mod_invoice' },
  { key: 'receipt', icon: 'receipt', label: 'mod_receipt' },
  { key: 'outbox', icon: 'mail-pause', label: 'mod_outbox' },
  { key: 'pettycash', icon: 'coins', label: 'mod_pettycash' },
  { key: 'bank_acc', icon: 'building-bank', label: 'mod_bank_acc' },
  { key: 'bank_legal', icon: 'scale', label: 'mod_bank_legal' },
  { key: 'bank_client', icon: 'users', label: 'mod_bank_client' },
  { key: 'taken_return', icon: 'arrows-exchange', label: 'tab_taken_return' },
  { key: 'doc_storage', icon: 'archive', label: 'tab_upload_doc' },
  { key: 'active_jobs', icon: 'briefcase', label: 'mod_active_jobs' },
  { key: 'closed_jobs', icon: 'folder-check', label: 'mod_closed_jobs' },
];
// Approvals' key, its Portal tab's: no page to pick, its button comes and goes by itself (report 6266a6e1).
const APPROVALS = 'outbox';
// Report edb33cb7: "Create a user page that can be launched from the quick launch menu" — each person's own work counts
// (components/UserPage). It is no module and on neither main menu, so the picker offers it to everyone in a group of its
// own after the ≡ menu's, and a dashboard's menu holds it after those pages, a green line on white like them (pagePill).
const USER_PAGE: Page = { key: 'user_page', icon: 'user-circle', label: 'up_title' };

// Each page as the Portal's content area (top-menu tabs) or its /m/ route in App.tsx (≡-menu modules) draws it; every
// one of them fills the full-height column beside the list. A module with no entry here is not offered.
const VIEWS: Record<string, () => React.ReactElement> = {
  email: () => <MailAppView />,
  contact: () => <Contact embedded />,
  calendar: () => <CalendarView />,
  quotation: () => <QuotationList />,
  invoice: () => <InvoiceList />,
  receipt: () => <ReceiptList />,
  outbox: () => <OutboxView group="docs" />,   // Accounting's list; Tracking, Filing and job-complete emails have the Portal's own buttons (report e58ddb81)
  pettycash: () => <PettyCashView />,
  bank_acc: () => <BankStatementView scope="accounting" />,
  bank_legal: () => <BankStatementView scope="legal" />,
  bank_client: () => <BankStatementView scope="client" />,
  taken_return: () => <TakenReturnView />,
  doc_storage: () => <DocStorageView />,
  active_jobs: () => <ActiveJobsList />,
  closed_jobs: () => <ClosedJobsList />,
  peak: () => <Peak />,
  slf_property: () => <SlfProperty />,
  slf_license: () => <ServiceDesk desk="license" />,
  slf_visa: () => <ServiceDesk desk="visa" />,
  slf_company: () => <ServiceDesk desk="company" />,
  tax: () => <Tax />,
  ai_counsel: () => <AiCounsel />,
  panda_marketing: () => <PandaMarketing />,
  bookkeeping: () => <Bookkeeping />,
  accounting_books: () => <AccountingBooks />,
  fee_board: () => <FeeBoardView />,
  workflows: () => <Workflows />,
  services: () => <Services />,
  staff_dashboard: () => <StaffDashboard />,
  finance_report: () => <FinanceReport />,
  portal_accounts: () => <PortalAccounts />,
  bugify: () => <Bugify />,
  user_page: () => <UserPageView />,
};

// The pages this user may put here, by the Portal's own rules (Portal/index.tsx: canSeeTab, canEmail / canContact and
// myModules), so the picker offers the doors the Portal shows them — all but Approvals, which is no page to pick
// (report 6266a6e1): `approvals` is its page for a user whose Approvals button may come by itself, by canSeeTab's rule
// less the wait, which FocusDashboard watches; null for anyone else. One difference: Email follows me.modules alone —
// its mailboxes are asked of /api/accounts, and a refused call pages the admins. A module both menus hold (Email,
// Filing, Contacts) is offered once, as its top-menu tab.
function pagesFor(me: Me): { top: Page[]; menu: Page[]; own: Page[]; approvals: Page | null } {
  const admin = me.role === 'admin';
  const has = (k: string) => me.modules.includes(k);
  const slfWorld = isSharedWorld(me.activeCompanyId || me.companyId, me.sharedWorld);
  const NON_SLF_TABS = new Set(['quotation', 'invoice', 'receipt', 'outbox', 'active_jobs', 'closed_jobs', 'pettycash']);
  const PEAK_TABS = new Set(['quotation', 'invoice', 'receipt', 'outbox']);
  const tabOk = (k: string) => k === 'email' ? slfWorld && has('email')
    : k === 'contact' ? slfWorld && (admin || has('contact'))
    : (slfWorld || NON_SLF_TABS.has(k))
      && (k !== 'outbox' || !!me.approves?.quotation || !!me.approves?.invoice_receipt || !!me.approves?.invoice_receipt_email)
      && (admin || (PEAK_TABS.has(k) ? has('peak') : has(k)));
  const top = TOP_PAGES.filter(p => p.key !== APPROVALS && tabOk(p.key));
  // The ≡ menu leaves out what Peak and the two containers fold in and what already has a top-menu tab.
  const canPeak = admin || has('peak');
  const folded = new Set(['quotation', 'invoice', 'receipt', ...(canPeak ? ['contact', 'services', 'bookkeeping'] : []),
    ...CONTAINER_SUB_KEYS,
    'bank_acc', 'bank_legal', 'bank_client', 'pettycash', 'active_jobs', 'closed_jobs', 'taken_return', 'calendar']);
  const NON_SLF_MODULES = new Set(['peak', 'workflows', 'staff_dashboard', 'bugify', 'pettycash']);
  const menu = MODULES.filter(m => {
    if (folded.has(m.key) || top.some(p => p.key === m.key) || !VIEWS[m.key]) return false;
    if (!slfWorld && !NON_SLF_MODULES.has(m.key)) return false;
    if (CONTAINER_SUBS[m.key]) return admin || CONTAINER_SUBS[m.key].some(has);
    return has(m.key);
  }).map(m => ({ key: m.key, icon: m.icon, label: `mod_${m.key}` }));
  // The User page is everyone's (report edb33cb7): whose counts it may show is its route's to decide.
  return { top, menu, own: [USER_PAGE], approvals: tabOk(APPROVALS) ? TOP_PAGES.find(p => p.key === APPROVALS) || null : null };
}

// A dashboard: 3–15 pages, with Email among them at least one mailbox, and a menu style.
const fits = (d: Dash) => d.items.length >= MIN_PAGES && d.items.length <= MAX_PAGES
  && (!d.items.includes('email') || d.mailboxes.length > 0) && !!d.layout;
// The same pages in the same order, the same mailboxes in any order, and the same menu style.
const sameDash = (a: Dash, b: Dash | null) => !!b && a.items.join() === b.items.join()
  && [...a.mailboxes].sort().join() === [...b.mailboxes].sort().join() && a.layout === b.layout;

// A page's colours down the column and along the top bar: its top-menu button's (menuPill), the open one filled. A page
// with none — every ≡-menu module — a green line on white, its icon and name in green, open or not (report 020e6911,
// above), where menuPill gives it a grey line on nothing and the open one a solid green fill; the toolbars that share
// menuPill keep that look. The open one's line is twice as thick, a 1px ring inside its border, so the button keeps its
// size and its words don't move.
const pagePill = (k: string, on: boolean): React.CSSProperties => tabColor(k) ? menuPill(k, on)
  : { borderColor: 'var(--accent)', background: 'var(--bg-surface)', color: 'var(--accent)', boxShadow: on ? 'inset 0 0 0 1px var(--accent)' : undefined };

// The open page's create + is drawn by the page itself, in its own bottom-right corner, in every layout (report
// 67f70fbd, above): this dashboard draws no + of its own on any menu button, and the small circle that floated against
// the side column's button between the 2nd opening of report 18577795 and that one is gone with it.

// ── Side menu: the column's width (report f4ef1615, above) ────────────────────────────────────────────────────────
// Its right-hand edge drags it from SIDE_W_MIN to SIDE_W_MAX; narrower than SIDE_ICONS_BELOW every button is its icon
// alone. The width is the person's own, kept in this browser under their login (mf_qlSideW:<userId>), as their
// mailbox order is (store: accountOrderKey); back at the full width, nothing is kept.
const SIDE_W = 210;            // the full width, all the column ever had before: where a double-click on icons only puts it
const SIDE_W_MIN = 56;         // icons only: a button 35px across inside the column's 10px each side
const SIDE_W_MAX = 320;
const SIDE_ICONS_BELOW = 120;  // narrower than this, icons only
/** A width the column may be given: whole pixels, from icons only to SIDE_W_MAX. */
const sideWidth = (w: number): number => Math.min(SIDE_W_MAX, Math.max(SIDE_W_MIN, Math.round(w)));
const sideWKey = (userId: string) => `mf_qlSideW:${userId}`;
function readSideW(userId: string | undefined): number {
  if (!userId) return SIDE_W;
  try { const w = Number(localStorage.getItem(sideWKey(userId))); return w ? sideWidth(w) : SIDE_W; } catch { return SIDE_W; }
}
function saveSideW(userId: string | undefined, w: number): void {
  if (!userId) return;
  try { if (w === SIDE_W) localStorage.removeItem(sideWKey(userId)); else localStorage.setItem(sideWKey(userId), String(w)); }
  catch { /* a private window: this tab keeps the width, the next opens at the full one */ }
}

export default function FocusDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useStore(s => s.me);
  const setMailboxFilter = useStore(s => s.setMailboxFilter);
  const [params, setParams] = useSearchParams();
  const pages = useMemo<{ top: Page[]; menu: Page[]; own: Page[]; approvals: Page | null }>(() => (me ? pagesFor(me) : { top: [], menu: [], own: [], approvals: null }), [me]);
  // An approver's Approvals page too, so its button, when it comes, is named and drawn as the others are.
  const pageOf = useMemo(() => new Map<string, Page>([...pages.top, ...pages.menu, ...pages.own, ...(pages.approvals ? [pages.approvals] : [])].map(p => [p.key, p] as [string, Page])), [pages]);
  // A dashboard's pages in the picker's order, whatever order they were ticked in (report ad70923c, 2nd opening): the
  // top menu's tabs as the main system's top menu has them (TOP_PAGES), so each colour group stays together, then the ≡
  // menu's modules, none of which has a top-menu colour, as one group in the ≡ menu's order, then the User page (report
  // edb33cb7). What this user can no longer open drops out, and so does Approvals, which the picker doesn't offer
  // (report 6266a6e1).
  const ordered = (items: string[]) => [...pages.top, ...pages.menu, ...pages.own].filter(p => items.includes(p.key)).map(p => p.key);

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dashId, setDashId] = useState<string | null>(null);   // the saved dashboard this tab shows; null = a new one
  const [savedName, setSavedName] = useState('');              // its name on the server; '' = not saved yet
  const [saved, setSaved] = useState<Dash | null>(null);        // what the server keeps, as far as this user can use it
  const [dash, setDash] = useState<Dash | null>(null);          // the one this tab shows
  const [draft, setDraft] = useState<Dash | null>(null);        // the picker's, while it is open
  const [mailboxes, setMailboxes] = useState<Mailbox[] | null>(null);   // every mailbox the user can open; null = unknown
  const [asking, setAsking] = useState(false);                  // the save question on Exit
  const [name, setName] = useState('');                         // its Name box
  const [nameErr, setNameErr] = useState('');                   // why the server refused that Save, under the box
  const [busy, setBusy] = useState(false);
  const leaving = useRef(false);
  // The chat button and its pop-up (report 32621dcf): no page, so it is outside `dash` entirely.
  const chat = useChatUnread();
  const [chatOpen, setChatOpen] = useState(false);
  // Reduce ⤡ on the full-screen chat page hands the window back to the tab it was expanded from (report 14dc81ac):
  // where that tab is this one, the pop-up opens again here, over the dashboard the reader left it on.
  useChatBack(() => setChatOpen(true));
  // Nothing here holds the open page's + any more (report 67f70fbd): the page draws it in its own bottom-right corner,
  // so this dashboard is never told what it does.

  // The side column's width (report f4ef1615; SIDE_W above) and the drag on its edge, as the chat list's edge works
  // (Chat/ChatPanel, report 8e638d38): which pointer holds it — a mouse, a finger or a pen, one at a time — where it
  // was pressed, and the width the column had then and has now. Each move is a width of its own, so the column follows
  // the hand; it is stored once, on letting go, and only if it changed, so a press that goes nowhere (the two of a
  // double-click among them) stores nothing.
  const [sideW, setSideW] = useState(() => readSideW(me?.userId));
  const [grabbing, setGrabbing] = useState(false);
  const [edgeHot, setEdgeHot] = useState(false);   // a pointer is on the edge: it is drawn in the accent
  const grab = useRef<{ id: number; x: number; from: number; to: number } | null>(null);
  const pressEdge = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || grab.current) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the drag still follows while the pointer is on the edge */ }
    grab.current = { id: e.pointerId, x: e.clientX, from: sideW, to: sideW };
    setGrabbing(true);
  };
  const dragEdge = (e: React.PointerEvent) => {
    const g = grab.current;
    if (!g || e.pointerId !== g.id) return;
    g.to = sideWidth(g.from + e.clientX - g.x);
    setSideW(g.to);
  };
  const dropEdge = (e: React.PointerEvent) => {
    const g = grab.current;
    if (!g || e.pointerId !== g.id) return;
    grab.current = null;
    setGrabbing(false);
    if (g.to !== g.from) saveSideW(me?.userId, g.to);
  };
  // A double-click: icons only from any width wider than that, the full width from icons only.
  const flipEdge = () => {
    const w = sideW < SIDE_ICONS_BELOW ? SIDE_W : SIDE_W_MIN;
    setSideW(w);
    saveSideW(me?.userId, w);
  };

  // The filter goes into the store before the page is drawn, so Email never opens on a mailbox it leaves out.
  const use = (d: Dash) => {
    const next = { items: d.items, mailboxes: d.items.includes('email') ? d.mailboxes : [], layout: d.layout };
    setMailboxFilter(next.items.includes('email') ? next.mailboxes : null);
    setDash(next);
    setDraft(null);
  };
  // Leaving /focus (the logo of a page drawn here goes to the Dashboard): every mailbox shows again.
  useEffect(() => () => setMailboxFilter(null), []);   // eslint-disable-line react-hooks/exhaustive-deps

  // The dashboard named in ?d=, less what this user can no longer open, in the picker's order. ?new=1, no ?d=, or an id
  // the user has no dashboard under, and the page starts a new one in the picker; so does a saved one with too little of
  // it left, under its own name. One saved with Approvals among its pages comes up without it (ordered), so it counts
  // toward neither the 3 nor the 12 (report 6266a6e1); its button comes by itself instead.
  useEffect(() => {
    let alive = true;
    const want = params.get('d') || '';
    Promise.all([
      // undefined = the server couldn't be asked; null = nothing to ask for
      want ? api.listFocusDashes().then(r => r, () => undefined) : Promise.resolve(null),
      pageOf.has('email') ? api.getAccounts().then((rows: any[]) => rows, () => null) : Promise.resolve([]),
    ]).then(([r, rows]) => {
      if (!alive) return;
      const boxes: Mailbox[] | null = Array.isArray(rows) ? rows.map((a: any) => ({ id: a.id, name: a.name, email: a.email, color: a.color })) : null;
      setMailboxes(boxes);
      setLoadFailed(r === undefined);
      const d: FocusDash | undefined = (r?.dashboards || []).find((x: FocusDash) => x.id === want);
      // One saved in the order its pages were ticked comes up re-sorted, and as that is also what `saved` holds, Exit
      // doesn't ask to save it for that alone.
      const items = ordered(d?.items || []);
      const start: Dash = {
        items,
        mailboxes: items.includes('email') ? (d?.mailboxes || []).filter(id => !boxes || boxes.some(b => b.id === id)) : [],
        // A saved one keeps its menu style, the side column if it was saved before there was a choice; a new one is asked.
        layout: d ? (d.layout === 'top' || d.layout === 'bottom' ? d.layout : 'side') : null,
      };
      if (d) {
        setDashId(d.id);
        setSavedName(d.name);
        setName(d.name);
        setSaved(start);
      }
      if (fits(start)) use(start); else setDraft(start);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // How much waits for this approver's approval — the number in the main top menu's red bubble on Approvals — asked of
  // /api/nav-badges as the Portal asks it (Portal/index.tsx): at once, every minute after, and on coming back to this
  // tab more than 30 seconds after the last ask. Only a user whose Approvals button may come asks (report 6266a6e1).
  const [waiting, setWaiting] = useState(0);
  useEffect(() => {
    if (!pages.approvals) return;
    let stopped = false;
    let lastPoll = 0;
    const poll = async () => {
      if (stopped) return;
      lastPoll = Date.now();
      try { const r: any = await api.navBadges(); if (!stopped && r) setWaiting(Number(r.outbox) || 0); } catch { /* next poll retries */ }
    };
    poll();
    const iv = setInterval(poll, 60_000);
    const onVis = () => { if (document.visibilityState === 'visible' && Date.now() - lastPoll > 30_000) poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { stopped = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [pages.approvals]);

  // What Exit offers to save: the shortlist in use, or the picker's while it is open on a complete one. A name typed
  // in Exit's question and then cancelled is not saved yet either.
  const candidate = draft && fits(draft) ? draft : dash;
  const unsaved = !!candidate && (!sameDash(candidate, saved) || name.trim() !== savedName);
  // Closing the tab outright (or reloading it) with an unsaved shortlist: the browser asks "Leave site?" — a page can
  // no longer put its own words or a Save button in that box, which is why Exit asks first.
  const unsavedRef = useRef(false);
  unsavedRef.current = unsaved;
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!unsavedRef.current || leaving.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  useEffect(() => {
    if (!asking) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) setAsking(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [asking, busy]);

  // Closes the tab Quick Launch opened. One the browser won't let a page close (not opened from a page) goes to the
  // Dashboard instead.
  const close = () => {
    leaving.current = true;
    setAsking(false);
    window.close();
    window.setTimeout(() => { if (!window.closed) navigate('/', { replace: true }); }, 300);
  };
  const exit = () => { if (unsaved) { setNameErr(''); setAsking(true); } else close(); };
  // Save: the dashboard that was opened is updated, a new one is added to the rocket's list.
  const saveAndClose = async () => {
    const nm = name.trim();
    if (!candidate || busy || !nm) return;
    setBusy(true);
    setNameErr('');
    const body = { name: nm, items: candidate.items, mailboxes: candidate.items.includes('email') ? candidate.mailboxes : [], layout: candidate.layout || 'side' };
    try {
      if (dashId) await api.updateFocusDash(dashId, body);
      else await api.createFocusDash(body);
      setSaved(candidate);
      setSavedName(nm);
      close();
    } catch (e: any) {
      // The server's reason, under the Name box: the name is taken, there is no room for another, or the dashboard
      // opened here was deleted from the rocket's list meanwhile, so the next Save keeps it as a new one.
      const code = e?.payload?.code;
      if (code === 'not_found') { setDashId(null); setSaved(null); setSavedName(''); }
      setNameErr(code === 'name_taken' ? t('ql_name_taken', { name: nm })
        : code === 'too_many' ? t('ql_too_many', { max: e.payload.max })
        : code === 'not_found' ? t('ql_gone')
        : t('ql_save_failed'));
    } finally {
      setBusy(false);
    }
  };

  // The page open on the right: the one asked for in ?item=, else the top of the list. Each page starts afresh — the
  // last one's own ?tab= / ?v= / ?client= … don't follow it to the next. A saved dashboard's ?d= stays beside it.
  const asked = params.get('item') || '';
  // An approver's Approvals is up while something waits for them or while it is the open page, as the main top menu's
  // tab is (report 6266a6e1), and not before the dashboard has loaded, so there is always one to go back to.
  const approvalsUp = !!pages.approvals && !loading && (waiting > 0 || asked === APPROVALS);
  const active = dash ? (dash.items.includes(asked) || (approvalsUp && asked === APPROVALS) ? asked : dash.items[0]) : '';
  // The open page's view, made afresh only when another page opens rather than at every redraw of this page, so a drag
  // on the side column's edge (report f4ef1615) redraws the column at each step and leaves the page beside it be.
  const page = useMemo(() => (VIEWS[active] ? VIEWS[active]() : null), [active]);
  const goTo = (k: string) => setParams(dashId ? { d: dashId, item: k } : { item: k }, { replace: true });
  const show = (k: string) => { if (k !== active) goTo(k); };
  // A right-click on a page's button or its dock tile sends that page to a browser tab of its own instead of opening it
  // beside this menu (Calem, report ab347131; services/newTab) — the very address the main system's menus open:
  // /?tab=<key> for a page of the Dashboard's strip, /m/<key> for a ≡-menu module. The User page is the one page with
  // no address of its own (it is drawn here, VIEWS), so its button and its tile keep the browser's own menu.
  const pageUrl = (k: string) => (k === USER_PAGE.key ? '' : pageHref(k));
  // Done: the page that was open stays open if it is still on the list (its own settings in the address bar with it),
  // and so does Approvals, which is up while it is open; otherwise the top of the new list, afresh.
  const done = () => {
    if (!draft || !fits(draft)) return;
    const next = draft.items.includes(active) || active === APPROVALS ? active : draft.items[0];
    use(draft);
    if (next !== active) goTo(next);
  };

  // The browser tab reads the open page, under the name its button here shows, then "Dashboard" and the saved name —
  // "Tracking · Dashboard Banking" — so it follows the page as the main Dashboard's follows its open tab (report
  // 9b83b181, 2nd opening: "Make sure the browser tab names are also working inside the Dashboard/Quick Launch"), where
  // it read the dashboard alone whichever page was open. With no page open (loading, or the picker in its place) the
  // page drops out; until the dashboard is saved there is no name, so "Dashboard" stands alone, where every dashboard
  // read "Quick Launch" (its 1st opening). DocumentTitle names every other page and leaves this one to it, the only place
  // the name and the open page are known. The Quick Launch caption on the page stays.
  const openLabel = loading || draft ? '' : pageOf.get(active)?.label || '';
  // The tab's icon follows the open page as well, a dot in its top-menu button's colour, as on every other page (report
  // 17b55236, utils/tabIcon.ts); the logo while no page is open. A page with no top-menu colour — the User page, every
  // ≡-menu module — takes the white dot with a green ring, the look its bubble has in the menu here (pagePill, report
  // 07ffc4c6), which is why the ring is asked for whenever a page is open; it was the logo too.
  const openColor = openLabel ? tabColor(active) : undefined;
  useEffect(() => {
    // The same base as every other tab's title (App.tsx DocumentTitle): "<brand> System", else "Management System".
    const b = brand();
    const base = `${t('ql_doc_title', { name: savedName }).trim()} · ${b ? `${b} System` : 'Management System'}`;
    document.title = openLabel ? `${t(openLabel)} · ${base}` : base;
    setTabDot(openColor, !!openLabel);
  }, [openLabel, openColor, savedName, t]);
  // And the app-wide pop-ups follow it too (report c2846304, services/popupHome): a dashboard with Active Jobs
  // open is the Active Jobs tab as far as a job question is concerned, so it is asked here rather than in a tab
  // of its own. /focus is one address whatever page is open on it, which is why it says so itself.
  useDashPage(active);

  const toggle = (k: string) => setDraft(d => {
    if (!d) return d;
    if (d.items.includes(k)) return { ...d, items: d.items.filter(x => x !== k) };
    if (d.items.length >= MAX_PAGES) return d;
    // Email with only one mailbox to pick from: that one comes ticked.
    const boxes = k === 'email' && !d.mailboxes.length && mailboxes?.length === 1 ? [mailboxes[0].id] : d.mailboxes;
    // into its place in the picker's order, not onto the end
    return { ...d, items: ordered([...d.items, k]), mailboxes: boxes };
  });
  const toggleBox = (id: string) => setDraft(d => d && ({
    ...d, mailboxes: d.mailboxes.includes(id) ? d.mailboxes.filter(x => x !== id) : [...d.mailboxes, id],
  }));
  const pickLayout = (l: FocusLayout) => setDraft(d => d && ({ ...d, layout: l }));

  if (!me) return null;
  const editing = !!draft;
  const listed = draft ? draft.items : dash?.items || [];
  // The menu's buttons: those pages and, while it is up, an approver's Approvals in its place on the main system's top
  // menu, after Receipt (report 6266a6e1) — `listed` is already in that order, TOP_PAGES', the ≡ menu's, then the User page.
  const shown = approvalsUp ? [...TOP_PAGES, ...pages.menu, ...pages.own].map(p => p.key).filter(k => k === APPROVALS || listed.includes(k)) : listed;
  // Where the menu sits: the picker's answer while it is open, so a change shows at once; the side column until there
  // is one. A saved dashboard's is known once it has loaded, so until then there is no menu, rather than a side column
  // that turns into the top bar or the dock.
  const layout = (draft || dash)?.layout;
  const top = layout === 'top';
  const bottom = layout === 'bottom';
  const menuDisplay = loading && params.get('d') ? 'none' : 'flex';
  // The side column narrower than SIDE_ICONS_BELOW (report f4ef1615): every button down it is its icon alone, in the
  // middle of the button, and a red count sits on its top-right corner — 4px out at most, so it stays inside the 6px gap
  // above and inside the list's own padding, which a scrolling list would otherwise cut it at.
  const iconsOnly = !top && sideW < SIDE_ICONS_BELOW;
  // Down the left the column draws a line against the page beside it — but not against Email, whose own page is
  // white between its bubbles and carries no grey of its own since Calem's report 75cd6e0e (4th opening: "Remove
  // the grey lines in between each bubble"), this being the line down the left of it. The edge to take hold of
  // (below) is where it always was, so the column is dragged wider and narrower on Email as on any other page.
  const emailOpen = !loading && !draft && active === 'email';
  const iconBtn: React.CSSProperties = { position: 'relative', justifyContent: 'center', padding: '9px 0' };
  const iconCount: React.CSSProperties = { position: 'absolute', top: -4, right: -4, zIndex: 1 };
  const sideBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' };
  // On the top bar Change and Exit are as tall as the pages and the rocket beside them: 36px, a main-menu box's height.
  // Icons only, their icon alone in the middle.
  const ctrlBtn: React.CSSProperties = top ? { ...sideBtn, height: 36, padding: '0 12px', whiteSpace: 'nowrap' } : iconsOnly ? { ...sideBtn, padding: '7px 0' } : sideBtn;

  return (
    // With Bottom menu the page stops DOCK_ROOM short of the window's foot, so the dock at rest never covers it. While
    // the side column's edge is held nothing on the page can be selected, so a drag picks out no words on its way.
    <div style={{ height: '100vh', display: 'flex', flexDirection: top || bottom ? 'column' : 'row', paddingBottom: bottom ? DOCK_ROOM : 0, background: 'var(--bg-surface)', userSelect: grabbing ? 'none' : undefined, WebkitUserSelect: grabbing ? 'none' : undefined }}>
      {/* The shortlist, stacked down the left — or with Top menu along one bar across the top, as on the main system's
          top menu: the name first, then the pages, wrapping onto another line where the window is too narrow, and the
          rocket, Change and Exit at the right-hand end. Each page wears its top-menu button's colours (menuPill), the
          open one filled — a ≡-menu module, which has none, a green line on white, the open one's twice as thick
          (pagePill; report 020e6911). While the picker is open it shows the picker's list, not clickable. With Bottom
          menu the dock (Dock, below) takes its place.
          An approver's Approvals joins them by itself while it is up (report 6266a6e1), faded with the rest while the
          picker is open, the count of what waits in the main top menu's red bubble: on its top-right corner along the
          bar, as there, and at its right-hand end down the column, where a corner would sit on the button above.
          Down the left the column is as wide as the person last dragged it (report f4ef1615, sideW); narrower than
          120px every button is its icon alone, Approvals' and Chat's count on its corner (iconCount). */}
      {!bottom && <aside style={top
        ? { flexShrink: 0, display: menuDisplay, alignItems: 'center', gap: 10, padding: '13px 10px', borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface)' }
        : { width: sideW, flexShrink: 0, display: menuDisplay, flexDirection: 'column', position: 'relative', borderRight: emailOpen ? 'none' : '1px solid var(--border-strong)', background: 'var(--bg-surface)' }}>
        {/* A saved dashboard's name, under a small Quick Launch caption; a new one shows the title alone until saved.
            Icons only, the logo alone, the name in its tooltip. */}
        <div title={iconsOnly ? savedName || t('ql_title') : undefined} style={top
          ? { display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0, maxWidth: 220 }
          : iconsOnly ? { display: 'flex', justifyContent: 'center', padding: '14px 0 12px' }
          : { display: 'flex', alignItems: 'center', gap: 9, padding: '14px 14px 12px' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }} />
          {!iconsOnly && (
            <div style={{ minWidth: 0 }}>
              {savedName && (
                <div style={{ fontSize: 10.5, lineHeight: '14px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('ql_title')}</div>
              )}
              <div title={savedName || undefined} style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{savedName || t('ql_title')}</div>
            </div>
          )}
        </div>
        {/* the main system's divider between its logo menu and its tabs */}
        {top && <div style={{ width: 1, height: 24, background: 'var(--border)', flexShrink: 0 }} />}
        <nav style={top
          ? { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5 }
          : { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 10px 10px' }}>
          {shown.map(k => {
            const p = pageOf.get(k);
            if (!p) return null;
            const on = !editing && k === active;
            const count = k === APPROVALS ? waiting : 0;
            const url = editing ? '' : pageUrl(k);   // while the picker is open these are disabled: no right-click, no hint
            // Icons only (report f4ef1615) the page's name leads its tooltip, the right-click hint under it.
            const pageName = t(p.label);
            const hint = url ? t('nt_open', { page: pageName }) : '';
            return (
              // Every page's button sits in a box of its own (report 18577795), as big as the button and in its place
              // in the menu. It held the + that floated against the button; no menu carries one now (report 67f70fbd),
              // and the box stays — as big as the button it holds, in either menu, it moves and resizes nothing.
              <div key={k} style={{ position: 'relative', display: 'flex', alignItems: 'center', ...(top ? {} : { width: '100%' }) }}>
                <button onClick={editing ? undefined : () => show(k)} disabled={editing} aria-current={on ? 'page' : undefined}
                  title={iconsOnly && hint ? `${pageName}\n${hint}` : hint || pageName} aria-label={iconsOnly ? pageName : undefined} onContextMenu={url ? openInNewTab(url) : undefined}
                  style={top
                    ? { position: 'relative', display: 'flex', alignItems: 'center', gap: 5, height: 36, maxWidth: 240, padding: '0 10px', borderRadius: 'var(--radius-md)', border: '1px solid', fontSize: 12, lineHeight: '15px', fontWeight: 700, cursor: editing ? 'default' : 'pointer', ...pagePill(k, on), opacity: editing ? 0.7 : 1 }
                    : { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 10px', borderRadius: 'var(--radius-md)', border: '1px solid', fontSize: 12.5, lineHeight: '16px', fontWeight: 700, textAlign: 'left', cursor: editing ? 'default' : 'pointer', ...(iconsOnly ? iconBtn : {}), ...pagePill(k, on), opacity: editing ? 0.7 : 1 }}>
                  <i className={`ti ti-${p.icon}`} style={{ fontSize: top ? 14 : 15, flexShrink: 0 }} aria-hidden />
                  {!iconsOnly && <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pageName}</span>}
                  {/* down the column 2px into the padding above and below, so the button stays as tall as its neighbours */}
                  {count > 0 && <CountBubble n={count} style={top ? { position: 'absolute', top: -12, right: -5, zIndex: 1 } : iconsOnly ? iconCount : { margin: '-2px 0 -2px auto' }} />}
                </button>
                {/* No button carries the open page's create + (report 67f70fbd: "Put the add button back into its
                    original spot in the bottom right corner of the page"). It floated off the side column button's
                    right-hand edge between the 2nd opening of report 18577795 and that one; the page draws it in its
                    own bottom-right corner instead, as it does on its own /m/ route. */}
              </div>
            );
          })}
          {/* not in a column of icons only, too narrow for the words — the picker beside it asks the same */}
          {!listed.length && !loading && !iconsOnly && (
            <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-tertiary)', padding: '2px 4px' }}>{t(top ? 'ql_empty_top' : 'ql_empty')}</div>
          )}
          {/* Chat, by itself after the pages (report 32621dcf): it is no page, so it is never faded out with them
              while the picker is open and it opens the pop-up instead of changing what is showing. It has no
              top-menu colour, so it is a green line on white like a ≡-menu module (pagePill). */}
          <button onClick={() => setChatOpen(true)} title={t('chat_btn_tip')} aria-label={t('chat_title')}
            style={top
              ? { position: 'relative', display: 'flex', alignItems: 'center', gap: 5, height: 36, maxWidth: 240, padding: '0 10px', borderRadius: 'var(--radius-md)', border: '1px solid', fontSize: 12, lineHeight: '15px', fontWeight: 700, cursor: 'pointer', ...pagePill('chat', false) }
              : { position: 'relative', display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 10px', borderRadius: 'var(--radius-md)', border: '1px solid', fontSize: 12.5, lineHeight: '16px', fontWeight: 700, textAlign: 'left', cursor: 'pointer', ...(iconsOnly ? iconBtn : {}), ...pagePill('chat', false) }}>
            <i className="ti ti-message-circle" style={{ fontSize: top ? 14 : 15, flexShrink: 0 }} aria-hidden />
            {!iconsOnly && <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('chat_title')}</span>}
            {chat.unread > 0 && <CountBubble n={chat.unread} style={top ? { position: 'absolute', top: -12, right: -5, zIndex: 1 } : iconsOnly ? iconCount : { margin: '-2px 0 -2px auto' }} />}
          </button>
        </nav>
        <div style={top
          ? { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }
          : { display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderTop: '1px solid var(--border)' }}>
          {/* The rocket and the list of saved dashboards it drops, as on the main system's top menu (Portal). */}
          {top && <QuickLaunchMenu />}
          {!editing && dash && (
            <button onClick={() => setDraft({ ...dash })} title={t('ql_change_tip')} aria-label={iconsOnly ? t('ql_change') : undefined} style={ctrlBtn}>
              <i className="ti ti-adjustments-horizontal" style={{ fontSize: 15 }} aria-hidden />{!iconsOnly && t('ql_change')}
            </button>
          )}
          <button onClick={exit} title={t('ql_exit_tip')} aria-label={iconsOnly ? t('ql_exit') : undefined} style={ctrlBtn}>
            <i className="ti ti-door-exit" style={{ fontSize: 15 }} aria-hidden />{!iconsOnly && t('ql_exit')}
          </button>
        </div>
        {/* The column's right-hand edge, to drag it narrower or wider (report f4ef1615), made as the chat list's edge is
            (report 8e638d38): it answers a mouse, a finger or a pen, and a finger on it never scrolls the page instead
            (touch-action); a strip astride the line, 7px each side, with a short bar at its middle, so an iPad, which
            has no cursor to change, still shows there is something to take hold of; the whole line in the accent while
            a pointer is on it or holding it. The mouse's own press is kept off it, so it starts no selection. A
            double-click switches between icons only and the full width. */}
        {!top && (
          <div role="separator" aria-orientation="vertical" aria-label={t('ql_side_resize')} title={t('ql_side_resize')}
            onMouseDown={e => e.preventDefault()}
            onPointerDown={pressEdge} onPointerMove={dragEdge} onPointerUp={dropEdge} onPointerCancel={dropEdge}
            onPointerEnter={() => setEdgeHot(true)} onPointerLeave={() => setEdgeHot(false)} onDoubleClick={flipEdge}
            style={{ position: 'absolute', top: 0, bottom: 0, right: -8, width: 15, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'col-resize', touchAction: 'none' }}>
            <span style={{ width: 3, height: edgeHot || grabbing ? '100%' : 28, borderRadius: 2, background: edgeHot || grabbing ? 'var(--accent)' : 'var(--border-strong)', transition: 'background 0.15s' }} aria-hidden />
          </div>
        )}
      </aside>}
      {bottom && (
        <Dock items={shown} empty={!listed.length} waiting={waiting} pageOf={pageOf} active={editing ? '' : active} editing={editing} onOpen={show}
          chatUnread={chat.unread} onChat={() => setChatOpen(true)}
          onChange={!editing && dash ? () => setDraft({ ...dash }) : undefined} onExit={exit} />
      )}

      <main className="ql-main" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* A ≡-menu page drawn whole (Peak, Tax, Bugify …) is as tall as the window (height 100vh), as on its own /m/
            tab, and beside the side column it fits. Below the top bar, or above the dock's room, there is less room, so
            there it fills what is left instead of losing its foot under the bar; only !important outranks the page's own
            inline height. */}
        {(top || bottom) && <style>{'.ql-main > [style*="height: 100vh"] { height: auto !important; flex: 1 1 0; min-height: 0; }'}</style>}
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ti ti-loader" style={{ fontSize: 28, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
          </div>
        ) : draft ? (
          <PagePicker draft={draft} pages={pages} mailboxes={mailboxes} loadFailed={loadFailed}
            onToggle={toggle} onToggleBox={toggleBox} onLayout={pickLayout} onDone={done}
            onCancel={dash ? () => setDraft(null) : undefined} />
        ) : dash && VIEWS[active] ? (
          // keyed, so moving between two pages drawn by the same view (the three Bank ones) starts the next afresh.
          // No host, in any of the three layouts (report 67f70fbd): the page keeps its create + in its own bottom-right
          // corner, label bubble included, exactly as on its own /m/ route — the take-over hand that put it on a menu
          // button is gone. Under a dock that corner is where the bar stands, so the page is told how much room the bar
          // takes (FabLift, DOCK_ROOM) and lifts its + that far, clear of it; under a top bar or a side column there is
          // nothing in the way and the lift is 0.
          <FabHost.Provider value={null}>
            <FabLift.Provider value={bottom ? DOCK_ROOM : 0}>
              <React.Fragment key={active}>{page}</React.Fragment>
            </FabLift.Provider>
          </FabHost.Provider>
        ) : null}
      </main>

      {/* The chat pop-up, over the page that is showing (report 32621dcf). Closing it asks for the unread count
          again, so the button's bubble empties without waiting for the next 30-second ask. */}
      {chatOpen && <ChatPanel onClose={() => { setChatOpen(false); chat.refresh(); }} />}

      {/* And its on-screen notices, step 3 of that report: a card at the bottom left for each message that arrives,
          which opens the chat pop-up on that chat over the page showing here. The cards keep away while either
          pop-up is open, so only one is ever on screen (components/Chat/ChatToast). */}
      <ChatToast />

      {asking && candidate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 460, maxWidth: '94vw', boxShadow: 'var(--shadow-md)', padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <i className="ti ti-device-floppy" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
              <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.4 }}>{t('ql_save_q')}</div>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>{t('ql_save_body')}</div>
            <div style={{ marginTop: 10, padding: '8px 11px', borderRadius: 10, background: 'var(--c-gray-50)', border: '1px solid var(--border)', fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, color: 'var(--text-primary)' }}>
              {ordered(candidate.items).map(k => t(pageOf.get(k)?.label || k)).join(' · ')}
              {/* and where they sit, which may be all that changed */}
              <div style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>
                {t('ql_layout')}: {t(LAYOUTS.find(l => l.key === candidate.layout)?.label || 'ql_layout_side')}
              </div>
            </div>
            {/* The name the rocket's list shows it under: blank for a new dashboard, and Save waits for one. */}
            <label style={{ display: 'block', marginTop: 14 }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>{t('ql_name')}</span>
              <input autoFocus value={name} maxLength={MAX_NAME} aria-invalid={!!nameErr}
                onChange={e => { setName(e.target.value); setNameErr(''); }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); saveAndClose(); } }}
                style={{ width: '100%', padding: '8px 11px', borderRadius: 9, border: `1px solid ${nameErr ? '#F04438' : 'var(--border-strong)'}`, background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13 }} />
            </label>
            {nameErr && (
              <div role="alert" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45, color: '#B42318' }}>{nameErr}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
              <button onClick={() => setAsking(false)} disabled={busy}
                style={{ padding: '9px 14px', borderRadius: 9, border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                {t('ql_cancel')}
              </button>
              <button onClick={close} disabled={busy}
                style={{ padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                {t('ql_dont_save')}
              </button>
              <button onClick={saveAndClose} disabled={busy || !name.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy || !name.trim() ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : name.trim() ? 1 : 0.5 }}>
                <i className={`ti ti-${busy ? 'loader' : 'check'}`} style={{ fontSize: 14 }} aria-hidden />{t('ql_save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Approvals' count (report 6266a6e1): the main top menu's red bubble (Portal: badgeSpan), its number trimmed to the
// figures so they sit in the middle of the circle, 99+ past 99. Where it sits is the caller's: a corner or a row's end.
function CountBubble({ n, style }: { n: number; style: React.CSSProperties }) {
  return (
    <span style={{ minWidth: 20, height: 20, padding: '0 3px', borderRadius: 999, background: '#DC2626', color: '#fff', fontSize: 11.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, boxSizing: 'border-box', flexShrink: 0, boxShadow: '0 0 0 1.5px var(--bg-surface)', ...style }}>
      <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{n > 99 ? '99+' : n}</span>
    </span>
  );
}

// ── Bottom menu: the dock (report b1e82c89) ──────────────────────────────────────────────────────────────────────
// "Add a bottom menu as well where you have separate tiles and you hover over them from the center (and click) like an
// Apple Mac." One bar fixed to the foot of the window, centred, its corners rounded and its background a little
// see-through, holding a tile per page — the page's icon in white on its top-menu button's colour (tabColor); a ≡-menu
// module, which has none, its icon in green on white inside a green line, as in the other two menus (report 020e6911)
// — in the other menus' order, then a divider, Change and Exit. As on the Mac's Dock, the tile under the pointer grows
// the most and its neighbours less the further they are from the pointer (magnify); a tile's name shows in a small
// bubble above it while the pointer is on it, or Tab has reached it; and the open page has a small dot under its tile,
// whatever its colours. A click opens the page as the side column's and the top bar's buttons do.
// While the picker is open the tiles show its list, faded, at rest and not clickable; Exit still works.
// An approver's Approvals tile comes and goes by itself as the other menus' button does (report 6266a6e1), the count of
// what waits in the main top menu's red bubble on its top-right corner, where the Mac's Dock badges a tile; the "tick
// the pages" note goes by the pages picked, Approvals not among them (empty).
// The page ends DOCK_ROOM short of the window's foot, so the bar at rest covers none of it; a grown tile and its bubble
// rise over the page's lowest strip only while the pointer is on them, as the Dock's do. The bar sits under every
// pop-up, drop target and dialog a page opens (z-index 20; theirs start at 30), so none of them hides behind it, and
// over the pages' sticky rows (10 at most).
const DOCK_TILE = 51;     // a tile's side at rest: 44 until report 1d106fd6 made the tiles 15% larger
const DOCK_PEAK = 83;     // … right under the pointer (72 until then)
const DOCK_REACH = 150;   // how far from the pointer (px) a tile still grows
const DOCK_PAD = 6;       // inside the bar, above and beside the tiles
const DOCK_FOOT = 11;     // inside the bar, under the tiles: where the open page's dot sits
const DOCK_LIFT = 8;      // between the bar and the window's foot
// the bar at rest, its two 1px borders included, its lift, and a little air above it
const DOCK_ROOM = DOCK_LIFT + DOCK_FOOT + DOCK_TILE + DOCK_PAD + 2 + 6;

// The focus came from the keyboard, not a click: the browser's own test, where it has one (an older one says yes).
const byKeyboard = (el: Element) => { try { return el.matches(':focus-visible'); } catch { return true; } };

function Dock({ items, empty, waiting, pageOf, active, editing, onOpen, chatUnread, onChat, onChange, onExit }: {
  items: string[]; empty: boolean; waiting: number; pageOf: Map<string, Page>; active: string; editing: boolean;
  onOpen: (k: string) => void; chatUnread: number; onChat: () => void; onChange?: () => void; onExit: () => void;
}) {
  const { t } = useTranslation();
  const slots = useRef<(HTMLDivElement | null)[]>([]);       // each tile's box, in order: the pages, then Change and Exit
  const [sizes, setSizes] = useState<number[] | null>(null);  // their sides while the pointer is on the bar; null = at rest
  const [hover, setHover] = useState('');                     // the tile under the pointer
  const [focus, setFocus] = useState('');                     // the tile Tab has reached
  const pointer = useRef<number | null>(null);
  const frame = useRef(0);
  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);

  // Once a frame at most: each tile's side by how far the pointer is from its middle — DOCK_PEAK right under it, easing
  // off (a cosine) to DOCK_TILE at DOCK_REACH and beyond.
  const magnify = () => {
    frame.current = 0;
    const x = pointer.current;
    if (x === null) return;
    setSizes(slots.current.map(el => {
      if (!el) return DOCK_TILE;
      const r = el.getBoundingClientRect();
      const d = Math.min(Math.abs(x - (r.left + r.width / 2)) / DOCK_REACH, 1);
      return Math.round(DOCK_TILE + (DOCK_PEAK - DOCK_TILE) * (1 + Math.cos(Math.PI * d)) / 2);
    }));
  };
  // A mouse or a pen only: a finger has no hover, and its tap is a click.
  const move = (e: React.PointerEvent) => {
    if (editing || e.pointerType === 'touch') return;
    pointer.current = e.clientX;
    if (!frame.current) frame.current = window.requestAnimationFrame(magnify);
  };
  const leave = () => {
    pointer.current = null;
    window.cancelAnimationFrame(frame.current);
    frame.current = 0;
    setSizes(null);
    setHover('');
  };

  const tile = (i: number, key: string, icon: string, label: string, look: React.CSSProperties, onClick?: () => void, open = false, count = 0) => {
    const s = (!editing && sizes?.[i]) || DOCK_TILE;
    const ease = '.12s ease-out';
    // Right-click opens the page in a browser tab of its own (report ab347131, as the other two menus' buttons do):
    // the tiles are keyed by page, so a tile with a page behind it takes it — not Chat, Change or Exit, which are no
    // page, and not the User page, which has no address of its own. The hint goes in the tile's tooltip, as it does
    // on every other bubble; the name bubble this dock raises above the tile is untouched. While the picker is open
    // the tiles are not clickable, so there is no right-click on them either — as there is none on the other two
    // menus' buttons, which are disabled then.
    const url = !editing && pageOf.has(key) && key !== USER_PAGE.key ? pageHref(key) : '';
    return (
      <div key={key} ref={el => { slots.current[i] = el; }}
        onPointerEnter={e => { if (e.pointerType !== 'touch') setHover(key); }}
        onPointerLeave={() => setHover(h => (h === key ? '' : h))}
        style={{ position: 'relative', flexShrink: 0, width: s, height: s, transition: `width ${ease}, height ${ease}` }}>
        <button onClick={onClick} aria-label={label} aria-disabled={onClick ? undefined : true} aria-current={open ? 'page' : undefined}
          title={url ? t('nt_open', { page: label }) : undefined} onContextMenu={url ? openInNewTab(url) : undefined}
          onFocus={e => { if (byKeyboard(e.currentTarget)) setFocus(key); }} onBlur={() => setFocus(f => (f === key ? '' : f))}
          style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, borderRadius: '24%', cursor: onClick ? 'pointer' : 'default', ...look }}>
          <i className={`ti ti-${icon}`} style={{ fontSize: Math.round(s / 2), lineHeight: 1, transition: `font-size ${ease}` }} aria-hidden />
        </button>
        {count > 0 && <CountBubble n={count} style={{ position: 'absolute', top: -5, right: -5, zIndex: 1, pointerEvents: 'none' }} />}
        {/* No tile carries the open page's create + (report 18577795, 3rd opening: "Remove it from the top menu and the
            bottom dock."). It sat on the open tile's top-right corner between the 2nd opening and this one; the page
            draws it in its own bottom-right corner again, lifted clear of this bar (FabLift). */}
        {(hover || focus) === key && (
          <span style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 9, padding: '4px 10px', borderRadius: 7, background: 'var(--text-primary)', color: '#fff', fontSize: 12, fontWeight: 600, lineHeight: '16px', whiteSpace: 'nowrap', boxShadow: 'var(--shadow-md)', pointerEvents: 'none' }}>{label}</span>
        )}
        {open && (
          <span style={{ position: 'absolute', top: '100%', left: '50%', width: 5, height: 5, margin: '3px 0 0 -2.5px', borderRadius: '50%', background: 'var(--text-primary)' }} aria-hidden />
        )}
      </div>
    );
  };

  const listed = items.map(k => pageOf.get(k)).filter((p): p is Page => !!p);
  const ctrl: React.CSSProperties = { border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)' };
  let n = 0;   // the next tile's place in slots
  return (
    <nav aria-label={t('ql_title')} onPointerMove={move} onPointerLeave={leave}
      style={{ position: 'fixed', left: '50%', bottom: DOCK_LIFT, transform: 'translateX(-50%)', zIndex: 20, display: 'flex', alignItems: 'flex-end', gap: 6, height: DOCK_PAD + DOCK_TILE + DOCK_FOOT + 2, padding: `${DOCK_PAD}px ${DOCK_PAD + 2}px ${DOCK_FOOT}px`, borderRadius: 18, border: '1px solid var(--border-strong)', background: 'color-mix(in srgb, var(--bg-surface) 70%, transparent)', backdropFilter: 'blur(16px) saturate(1.8)', WebkitBackdropFilter: 'blur(16px) saturate(1.8)', boxShadow: '0 8px 28px rgba(0,0,0,0.14), 0 1px 4px rgba(0,0,0,0.08)' }}>
      {listed.map(p => {
        const c = tabColor(p.key);   // undefined for a ≡-menu module: green on white inside a green line (report 020e6911)
        return tile(n++, p.key, p.icon, t(p.label),
          { border: c ? 'none' : '1px solid var(--accent)', background: c || 'var(--bg-surface)', color: c ? '#fff' : 'var(--accent)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', opacity: editing ? 0.7 : 1 },
          editing ? undefined : () => onOpen(p.key), p.key === active, p.key === APPROVALS ? waiting : 0);
      })}
      {empty && (
        <span style={{ alignSelf: 'center', padding: '0 6px', fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{t('ql_empty_bottom')}</span>
      )}
      {/* Chat's tile, the last before the divider (report 32621dcf): it is no page, so it has no dot of its own and
          it stays clickable while the picker is open — it opens a pop-up over whatever is showing. No top-menu
          colour, so it is green on white inside a green line, as a ≡-menu module's tile is. */}
      {tile(n++, 'ql-chat', 'message-circle', t('chat_title'),
        { border: '1px solid var(--accent)', background: 'var(--bg-surface)', color: 'var(--accent)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
        onChat, false, chatUnread)}
      <div style={{ alignSelf: 'stretch', width: 1, margin: '6px 3px', background: 'var(--border-strong)', flexShrink: 0 }} aria-hidden />
      {onChange && tile(n++, 'ql-change', 'adjustments-horizontal', t('ql_change'), ctrl, onChange)}
      {tile(n++, 'ql-exit', 'door-exit', t('ql_exit'), ctrl, onExit)}
    </nav>
  );
}

// Change: a tick box per page, top-menu tabs first, then the ≡ menu's modules, then the User page under a heading of
// its own (report edb33cb7). The menu (the list on the left, the bar across the top or the dock along the foot) holds
// the ticked ones in this same order, whatever order they are ticked in (report ad70923c, 2nd opening — see ordered),
// and the number on a ticked box is its place there. Email ticked brings its mailboxes in under them. Last, Menu style:
// Side menu, Top menu or Bottom menu (dock), one of which must be picked.
function PagePicker({ draft, pages, mailboxes, loadFailed, onToggle, onToggleBox, onLayout, onDone, onCancel }: {
  draft: Dash; pages: { top: Page[]; menu: Page[]; own: Page[] }; mailboxes: Mailbox[] | null; loadFailed: boolean;
  onToggle: (k: string) => void; onToggleBox: (id: string) => void; onLayout: (l: FocusLayout) => void;
  onDone: () => void; onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const n = draft.items.length;
  const withEmail = draft.items.includes('email');
  const status = n < MIN_PAGES ? t('ql_need_min', { min: MIN_PAGES })
    : withEmail && !draft.mailboxes.length ? t('ql_need_mailbox')
    : !draft.layout ? t('ql_need_layout')
    : n >= MAX_PAGES ? t('ql_at_max', { max: MAX_PAGES })
    : '';
  const heading: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '18px 0 8px' };
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 };
  const tile = (on: boolean, off: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--bg-active)' : 'var(--bg-app)', cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 0 });
  const pageTile = (p: Page) => {
    const at = draft.items.indexOf(p.key);
    const on = at >= 0;
    const off = !on && n >= MAX_PAGES;
    return (
      <label key={p.key} style={tile(on, off)}>
        <input type="checkbox" checked={on} disabled={off} onChange={() => onToggle(p.key)} style={{ margin: 0, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'inherit' }} />
        <i className={`ti ti-${p.icon}`} style={{ fontSize: 16, color: tabColor(p.key) || 'var(--accent)', flexShrink: 0 }} aria-hidden />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t(p.label)}</span>
        {/* Its place in the menu, the figure trimmed to its cap height so it sits in the middle of the circle (as CountBubble's) */}
        {on && (
          <span style={{ minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: 'var(--accent)', color: '#fff', fontSize: 10.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box' }}>
            <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{at + 1}</span>
          </span>
        )}
      </label>
    );
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 28px 28px', background: 'var(--bg-surface)' }}>
      <div style={{ maxWidth: 920 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{t('ql_pick_title', { min: MIN_PAGES, max: MAX_PAGES })}</div>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: n >= MIN_PAGES && n <= MAX_PAGES ? 'var(--accent-text)' : 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 10px' }}>{t('ql_count', { n })}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>{t(draft.layout === 'top' ? 'ql_pick_hint_top' : draft.layout === 'bottom' ? 'ql_pick_hint_bottom' : 'ql_pick_hint')}</div>
        {loadFailed && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '7px 11px' }}>
            <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} aria-hidden />{t('ql_load_failed')}
          </div>
        )}

        {pages.top.length > 0 && <>
          <div style={heading}>{t('ql_grp_top')}</div>
          <div style={grid}>{pages.top.map(pageTile)}</div>
        </>}
        {pages.menu.length > 0 && <>
          <div style={heading}>{t('ql_grp_menu')}</div>
          <div style={grid}>{pages.menu.map(pageTile)}</div>
        </>}
        {pages.own.length > 0 && <>
          <div style={heading}>{t('ql_grp_own')}</div>
          <div style={grid}>{pages.own.map(pageTile)}</div>
        </>}

        {withEmail && <>
          <div style={heading}>{t('ql_mailboxes')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '-2px 0 8px', lineHeight: 1.5 }}>{t('ql_mailboxes_hint')}</div>
          {mailboxes && mailboxes.length ? (
            <div style={grid}>
              {mailboxes.map(b => {
                const on = draft.mailboxes.includes(b.id);
                const c = ACCOUNT_COLORS[b.color] || ACCOUNT_COLORS.teal;
                return (
                  <label key={b.id} title={b.email} style={tile(on, false)}>
                    <input type="checkbox" checked={on} onChange={() => onToggleBox(b.id)} style={{ margin: 0, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'inherit' }} />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.accent, flexShrink: 0 }} aria-hidden />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                      <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.email}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#B42318' }}>{t('ql_no_mailboxes')}</div>
          )}
        </>}

        {/* Where the pages sit. A new dashboard has no answer yet, and Done waits for one. */}
        <div style={heading}>{t('ql_layout')}</div>
        <div role="radiogroup" aria-label={t('ql_layout')} style={grid}>
          {LAYOUTS.map(l => {
            const on = draft.layout === l.key;
            return (
              <label key={l.key} style={tile(on, false)}>
                <input type="radio" name="ql-layout" checked={on} onChange={() => onLayout(l.key)} style={{ margin: 0, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'inherit' }} />
                <i className={`ti ti-${l.icon}`} style={{ fontSize: 16, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block' }}>{t(l.label)}</span>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 400, lineHeight: 1.4, color: 'var(--text-tertiary)' }}>{t(l.hint)}</span>
                </span>
              </label>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
          {status && <span style={{ flex: 1, minWidth: 180, fontSize: 12, color: 'var(--text-tertiary)' }}>{status}</span>}
          {onCancel && (
            <button onClick={onCancel}
              style={{ padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {t('ql_cancel')}
            </button>
          )}
          <button onClick={onDone} disabled={!fits(draft)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: fits(draft) ? 'pointer' : 'not-allowed', opacity: fits(draft) ? 1 : 0.5 }}>
            <i className="ti ti-check" style={{ fontSize: 14 }} aria-hidden />{t('ql_done')}
          </button>
        </div>
      </div>
    </div>
  );
}
